// src/pages/PhoneCapturePage.jsx
//
// The page a phone lands on after scanning the QR. Public by design — the
// session token in the URL is the credential, and it is scoped to one upload
// target, capped, and short-lived.
//
// Capture uses the OS camera app (<input capture="environment">) rather than an
// in-page getUserMedia stream: it needs no HTTPS, no permission plumbing, and it
// gives the phone's real camera — flash, autofocus, full sensor — which matters
// when the subject is a hallmark stamp or a printed document.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button, Typography, Spin, Alert, Space, Result, Progress, Tag,
} from 'antd';
import {
  CameraOutlined, PictureOutlined, CheckCircleOutlined,
  DeleteOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import {
  fetchPublicCaptureSession, uploadCaptureFile, deleteCaptureFile, finishCaptureSession,
} from '../services/captureApi';
import compressImage from '../components/PhoneCapture/compressImage';

const { Title, Text, Paragraph } = Typography;

/** Best-effort device coordinates; resolves null rather than rejecting. */
const readGeo = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  });

export default function PhoneCapturePage() {
  const { token } = useParams();

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [notice, setNotice] = useState(null);
  const [finished, setFinished] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchPublicCaptureSession(token);
      setSession(data);
    } catch (err) {
      setFatal(err?.response?.data?.message || 'This capture link is not valid.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;

    setBusy(true);
    setNotice(null);

    // Ask once per batch, not once per photo.
    const geo = session?.requireGeo ? await readGeo() : await readGeo().catch(() => null);
    if (session?.requireGeo && !geo) {
      setNotice({
        type: 'error',
        text: 'This photo needs your location. Allow location access for this site and try again.',
      });
      setBusy(false);
      return;
    }

    let done = 0;
    for (const raw of picked) {
      try {
        setProgress({ done, total: picked.length });
        const file = await compressImage(raw);
        const result = await uploadCaptureFile(token, file, { fileName: file.name, geo });
        setSession(result.session);
        done += 1;
        setProgress({ done, total: picked.length });
      } catch (err) {
        setNotice({
          type: 'error',
          text: err?.response?.data?.message || 'Upload failed. Check your signal and try again.',
        });
        break;
      }
    }

    if (done > 0 && done === picked.length) {
      setNotice({ type: 'success', text: `${done} photo${done > 1 ? 's' : ''} sent to the desktop.` });
    }
    setBusy(false);
    setProgress(null);
  };

  const handleDelete = async (fileId) => {
    setBusy(true);
    try {
      setSession(await deleteCaptureFile(token, fileId));
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.message || 'Could not remove that photo.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDone = async () => {
    setBusy(true);
    try {
      await finishCaptureSession(token);
      setFinished(true);
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.message || 'Could not finish.' });
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>{children}</div>
    </div>
  );

  if (loading) {
    return shell(
      <div style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>,
    );
  }

  if (fatal) {
    return shell(
      <Result
        status="warning"
        title="Link not usable"
        subTitle={fatal}
        extra={<Text type="secondary">Generate a fresh QR code on the desktop and scan it again.</Text>}
      />,
    );
  }

  if (finished) {
    return shell(
      <Result
        status="success"
        title="All done"
        subTitle={`${session?.uploadedCount || 0} photo(s) sent. You can close this page.`}
      />,
    );
  }

  const remaining = session?.remaining ?? 0;
  const atLimit = remaining <= 0;

  return shell(
    <>
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 }}>
        <Title level={4} style={{ marginBottom: 4 }}>{session?.target?.label}</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
          {session?.requestedBy ? `Requested by ${session.requestedBy}. ` : ''}
          Photos you take here appear instantly on the desktop.
        </Paragraph>
        <div style={{ marginTop: 8 }}>
          <Space size={4} wrap>
            <Tag>{session?.uploadedCount || 0} / {session?.maxFiles} sent</Tag>
            {session?.requireGeo && (
              <Tag icon={<EnvironmentOutlined />} color="blue">Location required</Tag>
            )}
          </Space>
        </div>
      </div>

      {notice && (
        <Alert
          type={notice.type}
          showIcon
          message={notice.text}
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {atLimit && (
        <Alert
          type="info"
          showIcon
          message={`Limit reached — ${session?.maxFiles} file(s) already sent.`}
          style={{ marginBottom: 12 }}
        />
      )}

      {progress && (
        <Progress
          percent={Math.round((progress.done / progress.total) * 100)}
          status="active"
          style={{ marginBottom: 12 }}
        />
      )}

      <input
        ref={cameraRef}
        type="file"
        accept={session?.accept || 'image/*'}
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept={session?.accept || 'image/*'}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />

      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Button
          type="primary"
          size="large"
          block
          icon={<CameraOutlined />}
          loading={busy}
          disabled={atLimit}
          onClick={() => cameraRef.current?.click()}
          style={{ height: 64, fontSize: 18 }}
        >
          Take Photo
        </Button>
        <Button
          size="large"
          block
          icon={<PictureOutlined />}
          disabled={busy || atLimit}
          onClick={() => galleryRef.current?.click()}
          style={{ height: 52 }}
        >
          Choose from Gallery
        </Button>
      </Space>

      {(session?.files || []).length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 12, marginTop: 12 }}>
          <Text strong>Sent</Text>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {session.files.map((f) => (
              <div key={f.fileId} style={{ position: 'relative' }}>
                <img
                  src={f.thumbnailUrl || f.url}
                  alt={f.name || 'Captured'}
                  style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }}
                />
                <Button
                  size="small"
                  danger
                  shape="circle"
                  icon={<DeleteOutlined />}
                  disabled={busy}
                  onClick={() => handleDelete(f.fileId)}
                  style={{ position: 'absolute', top: -6, right: -6 }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        block
        size="large"
        icon={<CheckCircleOutlined />}
        onClick={handleDone}
        disabled={busy}
        style={{ marginTop: 16, height: 52 }}
      >
        Done
      </Button>
    </>,
  );
}
